import React, { useEffect, useRef, useState } from 'react';
import { Loader, MapPin } from 'lucide-react';
import CesiumTerrainMap from './CesiumTerrainMap';

const DEFAULT_CENTER = [38.7223, -9.1393];
const METERS_PER_DEGREE_LAT = 111320;
const AUTO_LEG_COLOR = '#7c3aed';
const MANUAL_LEG_COLOR = '#2563eb';
const DRAFT_LEG_COLOR = '#f59e0b';

const getPointType = (point) => {
  const pointType = String(point?.pointType || '').trim();
  if (pointType) return pointType === 'bus_stop' ? 'popup_stop' : pointType;
  return point?.category === 'bus_stop' || point?.category === 'popup_stop'
    ? 'popup_stop'
    : 'stop';
};

const haversineMeters = ([lat1, lng1], [lat2, lng2]) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const earthRadius = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadius * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const getOverlayMetrics = (overlay) => {
  if (!overlay?.imageUrl || !Array.isArray(overlay?.center) || overlay.center.length !== 2) {
    return null;
  }

  const centerLat = parseFloat(overlay.center[0]);
  const centerLng = parseFloat(overlay.center[1]);
  const widthMeters = parseFloat(overlay.widthMeters);
  const aspectRatio = parseFloat(overlay.aspectRatio);

  if (
    !Number.isFinite(centerLat) ||
    !Number.isFinite(centerLng) ||
    !Number.isFinite(widthMeters) ||
    widthMeters <= 0 ||
    !Number.isFinite(aspectRatio) ||
    aspectRatio <= 0
  ) {
    return null;
  }

  const heightMeters = widthMeters / aspectRatio;
  const latHalfSpan = (heightMeters / 2) / METERS_PER_DEGREE_LAT;
  const lngMetersPerDegree = METERS_PER_DEGREE_LAT * Math.max(Math.cos((centerLat * Math.PI) / 180), 0.0001);
  const lngHalfSpan = (widthMeters / 2) / lngMetersPerDegree;

  return {
    center: [centerLat, centerLng],
    widthMeters,
    aspectRatio,
    bounds: [
      [centerLat - latHalfSpan, centerLng - lngHalfSpan],
      [centerLat + latHalfSpan, centerLng + lngHalfSpan],
    ],
    eastHandle: [centerLat, centerLng + lngHalfSpan],
    outline: [
      [centerLat - latHalfSpan, centerLng - lngHalfSpan],
      [centerLat - latHalfSpan, centerLng + lngHalfSpan],
      [centerLat + latHalfSpan, centerLng + lngHalfSpan],
      [centerLat + latHalfSpan, centerLng - lngHalfSpan],
    ],
  };
};

const LeafletItineraryMapView = ({
  points = [],
  itineraryMetadata = {},
  legs = [],
  mapOverlay = null,
  onMapViewChange = null,
  overlayEditor = null,
  stopPlacementEditor = null,
  popupStopEditor = null,
  manualEditor = null,
}) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const leafletRef = useRef(null);
  const markersRef = useRef([]);
  const polylineRef = useRef(null);
  const legPolylinesRef = useRef([]);
  const draftPolylineRef = useRef(null);
  const draftPointMarkersRef = useRef([]);
  const draftInsertMarkersRef = useRef([]);
  const imageOverlayRef = useRef(null);
  const overlayOutlineRef = useRef(null);
  const overlayCenterHandleRef = useRef(null);
  const overlayResizeHandleRef = useRef(null);
  const hasFitBoundsRef = useRef(false);
  const previousPointsSignatureRef = useRef('');
  const draftOverlayDragRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [draftOverlayTick, setDraftOverlayTick] = useState(0);

  const emitMapViewChange = () => {
    if (!mapInstanceRef.current || typeof onMapViewChange !== 'function') return;

    const map = mapInstanceRef.current;
    const bounds = map.getBounds();
    const center = map.getCenter();
    onMapViewChange({
      center: [center.lat, center.lng],
      zoom: map.getZoom(),
      bounds: [
        [bounds.getSouth(), bounds.getWest()],
        [bounds.getNorth(), bounds.getEast()],
      ],
    });
  };

  const clearOverlayEditorArtifacts = () => {
    overlayOutlineRef.current?.remove();
    overlayOutlineRef.current = null;
    overlayCenterHandleRef.current?.remove();
    overlayCenterHandleRef.current = null;
    overlayResizeHandleRef.current?.remove();
    overlayResizeHandleRef.current = null;
  };

  const getManualDraftScreenPoints = () => {
    if (!mapInstanceRef.current || !manualEditor?.isActive || !manualEditor?.renderDraftOverlay) {
      return null;
    }

    const map = mapInstanceRef.current;
    const draftPolyline = Array.isArray(manualEditor.draftPolyline) ? manualEditor.draftPolyline : [];

    return draftPolyline
      .map((point) => {
        if (!Array.isArray(point) || point.length !== 2) return null;
        const lat = parseFloat(point[0]);
        const lng = parseFloat(point[1]);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        const containerPoint = map.latLngToContainerPoint([lat, lng]);
        return { lat, lng, x: containerPoint.x, y: containerPoint.y };
      })
      .filter(Boolean);
  };

  const handleDraftPointPointerDown = (event, index) => {
    if (!mapInstanceRef.current || !manualEditor?.onDraftPointDrag) return;
    event.preventDefault();
    event.stopPropagation();

    const map = mapInstanceRef.current;
    const container = map.getContainer();
    const rect = container.getBoundingClientRect();
    const pointerId = event.pointerId;

    draftOverlayDragRef.current = { index, pointerId };
    map.dragging.disable();

    const cleanup = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
      map.dragging.enable();
      draftOverlayDragRef.current = null;
    };

    const handleMove = (moveEvent) => {
      if (draftOverlayDragRef.current?.pointerId !== pointerId) return;
      const x = moveEvent.clientX - rect.left;
      const y = moveEvent.clientY - rect.top;
      const nextLatLng = map.containerPointToLatLng([x, y]);
      manualEditor.onDraftPointDrag(index, [nextLatLng.lat, nextLatLng.lng]);
      setDraftOverlayTick((value) => value + 1);
    };

    const handleUp = (upEvent) => {
      if (draftOverlayDragRef.current?.pointerId !== pointerId) return;
      cleanup();
      const x = upEvent.clientX - rect.left;
      const y = upEvent.clientY - rect.top;
      const nextLatLng = map.containerPointToLatLng([x, y]);
      manualEditor.onDraftPointDrag(index, [nextLatLng.lat, nextLatLng.lng]);
      setDraftOverlayTick((value) => value + 1);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
  };

  const applyOverlayRotation = () => {
    if (!imageOverlayRef.current) return;
    const element = imageOverlayRef.current.getElement?.();
    if (!element) return;

    const baseTransform = (element.style.transform || '').replace(/\srotate\([^)]*\)/g, '').trim();
    const rotationDeg = Number.isFinite(mapOverlay?.rotationDeg) ? mapOverlay.rotationDeg : 0;
    element.style.transformOrigin = 'center center';
    element.style.transform = `${baseTransform} rotate(${rotationDeg}deg)`.trim();
  };

  const syncMapOverlay = () => {
    if (!mapInstanceRef.current || !leafletRef.current) return;

    const L = leafletRef.current;
    const map = mapInstanceRef.current;
    const overlayMetrics = getOverlayMetrics(mapOverlay);
    const isVisible = mapOverlay?.visible !== false;
    const opacity = typeof mapOverlay?.opacity === 'number' ? mapOverlay.opacity : 0.72;

    if (!mapOverlay?.imageUrl || !isVisible || !overlayMetrics) {
      imageOverlayRef.current?.remove();
      imageOverlayRef.current = null;
      clearOverlayEditorArtifacts();
      return;
    }

    const overlayBounds = L.latLngBounds(overlayMetrics.bounds);
    if (!imageOverlayRef.current) {
      imageOverlayRef.current = L.imageOverlay(mapOverlay.imageUrl, overlayBounds, { opacity }).addTo(map);
    } else {
      imageOverlayRef.current.setUrl(mapOverlay.imageUrl);
      imageOverlayRef.current.setBounds(overlayBounds);
      imageOverlayRef.current.setOpacity(opacity);
      if (!map.hasLayer(imageOverlayRef.current)) {
        imageOverlayRef.current.addTo(map);
      }
    }

    requestAnimationFrame(() => {
      applyOverlayRotation();
      imageOverlayRef.current?.bringToFront();
    });
  };

  const syncOverlayEditor = () => {
    if (!mapInstanceRef.current || !leafletRef.current) return;

    const L = leafletRef.current;
    const map = mapInstanceRef.current;
    const overlayMetrics = getOverlayMetrics(mapOverlay);

    clearOverlayEditorArtifacts();

    if (!overlayEditor?.isActive || !overlayMetrics) {
      return;
    }

    overlayOutlineRef.current = L.polygon(overlayMetrics.outline, {
      color: '#f59e0b',
      weight: 2,
      opacity: 0.9,
      dashArray: '8 6',
      fillOpacity: 0,
      interactive: false,
    }).addTo(map);

    const centerIcon = L.divIcon({
      className: '',
      html: '<div style="width:16px;height:16px;border-radius:9999px;background:#f59e0b;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.35);"></div>',
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });

    const resizeIcon = L.divIcon({
      className: '',
      html: '<div style="width:14px;height:14px;border-radius:4px;background:#0ea5e9;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.35);"></div>',
      iconSize: [14, 14],
      iconAnchor: [7, 7],
    });

    overlayCenterHandleRef.current = L.marker(overlayMetrics.center, {
      draggable: true,
      icon: centerIcon,
      title: 'Move overlay',
    }).addTo(map);
    overlayCenterHandleRef.current.on('drag', (event) => {
      const { lat, lng } = event.target.getLatLng();
      overlayEditor.onChange?.({ center: [lat, lng] });
    });

    overlayResizeHandleRef.current = L.marker(overlayMetrics.eastHandle, {
      draggable: true,
      icon: resizeIcon,
      title: 'Resize overlay',
    }).addTo(map);
    overlayResizeHandleRef.current.on('drag', (event) => {
      const { lat, lng } = event.target.getLatLng();
      const nextWidthMeters = Math.max(10, Math.round(haversineMeters(overlayMetrics.center, [lat, lng]) * 2));
      overlayEditor.onChange?.({ widthMeters: nextWidthMeters });
    });

    overlayOutlineRef.current.bringToFront();
    overlayCenterHandleRef.current?.bringToFront();
    overlayResizeHandleRef.current?.bringToFront();
  };

  const updateMapMarkers = () => {
    if (!mapInstanceRef.current || !leafletRef.current) return;

    const L = leafletRef.current;
    const map = mapInstanceRef.current;

    markersRef.current.forEach((marker) => marker?.remove());
    markersRef.current = [];

    polylineRef.current?.remove();
    polylineRef.current = null;

    legPolylinesRef.current.forEach((polyline) => polyline.remove());
    legPolylinesRef.current = [];

    draftPolylineRef.current?.remove();
    draftPolylineRef.current = null;

    draftPointMarkersRef.current.forEach((marker) => marker?.remove());
    draftPointMarkersRef.current = [];

    draftInsertMarkersRef.current.forEach((marker) => marker?.remove());
    draftInsertMarkersRef.current = [];

    const validPoints = points.filter((point) =>
      point.coordinates &&
      point.coordinates.latitude &&
      point.coordinates.longitude &&
      !Number.isNaN(parseFloat(point.coordinates.latitude)) &&
      !Number.isNaN(parseFloat(point.coordinates.longitude))
    );

    if (validPoints.length === 0) return;

    const pathCoordinates = validPoints.map((point) => [
      parseFloat(point.coordinates.latitude),
      parseFloat(point.coordinates.longitude),
    ]);

    validPoints.forEach((point, index) => {
      const isPopupStop = getPointType(point) === 'popup_stop';
      const markerIcon = L.divIcon({
        className: 'custom-number-icon',
        html: isPopupStop
          ? `
            <div style="background-color: #f59e0b; color: white; border-radius: 9999px; width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 18px; box-shadow: 0 2px 6px rgba(0,0,0,0.28); border: 2px solid white;">
              &#128712;
            </div>
          `
          : `<div style="background-color: #3b82f6; color: white; border-radius: 50%; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 14px;">${index + 1}</div>`,
        iconSize: isPopupStop ? [34, 34] : [28, 28],
        iconAnchor: isPopupStop ? [17, 17] : [14, 14],
      });

      const isSelectedStop = stopPlacementEditor?.selectedStopId && stopPlacementEditor.selectedStopId === point.id;
      const marker = L.marker(pathCoordinates[index], {
        icon: markerIcon,
        title: point.name,
        draggable: Boolean(stopPlacementEditor?.isActive),
      }).addTo(map);

      const popupContent = `
        <div style="max-width: 200px">
          <h3 style="margin: 0 0 8px; font-size: 16px">${isPopupStop ? 'Popup Stop' : `${index + 1}.`} ${point.name}</h3>
          ${point.category ? `<p style="margin: 0 0 5px; font-size: 12px"><strong>Category:</strong> ${point.category}</p>` : ''}
          ${point.estimatedDuration ? `<p style="margin: 0 0 5px; font-size: 12px"><strong>Duration:</strong> ${point.estimatedDuration} minutes</p>` : ''}
          ${point.description ? `<p style="margin: 0; font-size: 12px">${point.description.substring(0, 100)}${point.description.length > 100 ? '...' : ''}</p>` : ''}
        </div>
      `;

      marker.bindPopup(popupContent);
      if (isSelectedStop) {
        marker.bindTooltip('Selected stop', {
          permanent: true,
          direction: 'top',
          offset: [0, -16],
          className: 'selected-stop-tooltip',
        });
      }
      if (stopPlacementEditor?.isActive && stopPlacementEditor?.onMarkerDrag) {
        marker.on('dragend', (event) => {
          const { lat, lng } = event.target.getLatLng();
          stopPlacementEditor.onMarkerDrag(point.id, [lat, lng]);
        });
      }
      markersRef.current.push(marker);
    });

    if (legs?.length > 0) {
      legs.forEach((leg) => {
        if (!leg.polyline || leg.polyline.length < 2) return;
        const polyline = L.polyline(leg.polyline, {
          color: leg.route_source === 'manual' ? MANUAL_LEG_COLOR : AUTO_LEG_COLOR,
          weight: 4,
          opacity: 0.85,
        }).addTo(map);

        polyline.bindTooltip(
          `${leg.route_source === 'manual' ? 'Manual saved' : 'Auto'} - ${leg.route_mode || 'walking'} - ${Math.round(leg.distance_meters || 0)} m - ${Math.round((leg.duration_seconds || 0) / 60)} min`,
          { sticky: true }
        );
        legPolylinesRef.current.push(polyline);
      });
    } else if (pathCoordinates.length > 1) {
      polylineRef.current = L.polyline(pathCoordinates, {
        color: '#0066ff',
        weight: 3,
        opacity: 0.8,
        dashArray: '6 6',
      }).addTo(map);
    }

    const pointsSignature = JSON.stringify(pathCoordinates);
    const shouldFitBounds =
      !hasFitBoundsRef.current ||
      previousPointsSignatureRef.current !== pointsSignature;

    if (pathCoordinates.length > 0 && shouldFitBounds) {
      const bounds = L.latLngBounds(pathCoordinates);
      map.fitBounds(bounds, { padding: [30, 30] });
      if (map.getZoom() > 16) {
        map.setZoom(16);
      }
      hasFitBoundsRef.current = true;
      previousPointsSignatureRef.current = pointsSignature;
      emitMapViewChange();
    }

    if (manualEditor?.isActive && Array.isArray(manualEditor.draftPolyline) && manualEditor.draftPolyline.length >= 2) {
      draftPolylineRef.current = L.polyline(manualEditor.draftPolyline, {
        color: DRAFT_LEG_COLOR,
        weight: 5,
        opacity: 0.95,
        dashArray: '10 6',
      }).addTo(map);
    }

    if (
      manualEditor?.isActive &&
      !manualEditor?.renderDraftOverlay &&
      Array.isArray(manualEditor.draftPoints) &&
      manualEditor.draftPoints.length > 0
    ) {
      manualEditor.draftPoints.forEach((point, index) => {
        if (!Array.isArray(point) || point.length !== 2) return;

        const dragHandleIcon = L.divIcon({
          className: '',
          html: `
            <div style="
              width: 26px;
              height: 26px;
              border-radius: 9999px;
              background: ${DRAFT_LEG_COLOR};
              color: white;
              border: 2px solid white;
              box-shadow: 0 2px 6px rgba(0,0,0,0.28);
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 12px;
              font-weight: 700;
              cursor: move;
              pointer-events: auto;
              touch-action: none;
              user-select: none;
            ">${index + 1}</div>
          `,
          iconSize: [26, 26],
          iconAnchor: [13, 13],
        });

        const marker = L.marker(point, {
          draggable: true,
          icon: dragHandleIcon,
          title: `Waypoint ${index + 1}`,
          bubblingMouseEvents: false,
          zIndexOffset: 1500,
        }).addTo(map);

        marker.bindTooltip(`Waypoint ${index + 1}. Drag to adjust. Right click to remove.`, {
          sticky: true,
          direction: 'top',
        });

        marker.on('add', () => {
          const element = marker.getElement?.();
          if (element) {
            L.DomEvent.disableClickPropagation(element);
            L.DomEvent.disableScrollPropagation(element);
          }
        });

        marker.on('dragend', (event) => {
          const { lat, lng } = event.target.getLatLng();
          manualEditor.onDraftPointDrag?.(index, [lat, lng]);
        });

        marker.on('contextmenu', () => {
          manualEditor.onDraftPointRemove?.(index);
        });

        draftPointMarkersRef.current.push(marker);
      });
    }

    if (
      manualEditor?.isActive &&
      !manualEditor?.renderDraftOverlay &&
      Array.isArray(manualEditor.draftPolyline) &&
      manualEditor.draftPolyline.length >= 2 &&
      typeof manualEditor.onDraftPointInsert === 'function'
    ) {
      for (let i = 0; i < manualEditor.draftPolyline.length - 1; i += 1) {
        const from = manualEditor.draftPolyline[i];
        const to = manualEditor.draftPolyline[i + 1];
        if (!Array.isArray(from) || from.length !== 2 || !Array.isArray(to) || to.length !== 2) continue;

        const midpoint = [
          (parseFloat(from[0]) + parseFloat(to[0])) / 2,
          (parseFloat(from[1]) + parseFloat(to[1])) / 2,
        ];

        const insertIcon = L.divIcon({
          className: '',
          html: `
            <div style="
              width: 22px;
              height: 22px;
              border-radius: 9999px;
              background: white;
              color: ${DRAFT_LEG_COLOR};
              border: 2px solid ${DRAFT_LEG_COLOR};
              box-shadow: 0 2px 6px rgba(0,0,0,0.18);
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 14px;
              font-weight: 800;
              line-height: 1;
              cursor: pointer;
              pointer-events: auto;
              touch-action: none;
              user-select: none;
            ">+</div>
          `,
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        });

        const marker = L.marker(midpoint, {
          interactive: true,
          keyboard: false,
          icon: insertIcon,
          title: `Insert waypoint between ${i + 1} and ${i + 2}`,
          bubblingMouseEvents: false,
          zIndexOffset: 1400,
        }).addTo(map);

        marker.bindTooltip('Click to insert a waypoint here.', {
          sticky: true,
          direction: 'top',
        });

        marker.on('add', () => {
          const element = marker.getElement?.();
          if (element) {
            L.DomEvent.disableClickPropagation(element);
            L.DomEvent.disableScrollPropagation(element);
          }
        });

        marker.on('click', () => {
          manualEditor.onDraftPointInsert?.(i, midpoint);
        });

        draftInsertMarkersRef.current.push(marker);
      }
    }
  };

  const initMap = () => {
    if (!mapContainerRef.current || !leafletRef.current) return;

    const L = leafletRef.current;
    mapInstanceRef.current?.remove();
    hasFitBoundsRef.current = false;

    const lat = itineraryMetadata?.coordinates?.latitude
      ? parseFloat(itineraryMetadata.coordinates.latitude)
      : DEFAULT_CENTER[0];
    const lng = itineraryMetadata?.coordinates?.longitude
      ? parseFloat(itineraryMetadata.coordinates.longitude)
      : DEFAULT_CENTER[1];

    const map = L.map(mapContainerRef.current).setView([lat, lng], 13);
    mapInstanceRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    updateMapMarkers();
    syncMapOverlay();
    syncOverlayEditor();
    emitMapViewChange();
  };

  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const cssLink = document.createElement('link');
      cssLink.id = 'leaflet-css';
      cssLink.rel = 'stylesheet';
      cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(cssLink);
    }

    const loadLeaflet = async () => {
      try {
        if (!window.L && !leafletRef.current) {
          const script = document.createElement('script');
          script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          script.onload = () => {
            leafletRef.current = window.L;
            setMapLoaded(true);
          };
          script.onerror = () => setLoadError('Failed to load map library');
          document.head.appendChild(script);
        } else {
          leafletRef.current = window.L;
          setMapLoaded(true);
        }
      } catch (error) {
        console.error('Error loading Leaflet:', error);
        setLoadError('Failed to load map library');
      }
    };

    loadLeaflet();

    return () => {
      clearOverlayEditorArtifacts();
      imageOverlayRef.current?.remove();
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (mapLoaded && leafletRef.current) {
      initMap();
    }
  }, [mapLoaded]);

  useEffect(() => {
    if (mapInstanceRef.current && mapLoaded && leafletRef.current) {
      updateMapMarkers();
    }
  }, [points, legs, mapLoaded, manualEditor, popupStopEditor, stopPlacementEditor]);

  useEffect(() => {
    if (!mapInstanceRef.current || !manualEditor?.isActive || !manualEditor?.renderDraftOverlay) return undefined;

    const map = mapInstanceRef.current;
    const refreshDraftOverlay = () => setDraftOverlayTick((value) => value + 1);

    map.on('zoomend moveend resize viewreset', refreshDraftOverlay);
    return () => {
      map.off('zoomend moveend resize viewreset', refreshDraftOverlay);
    };
  }, [mapLoaded, manualEditor]);

  useEffect(() => {
    if (!mapInstanceRef.current) return undefined;

    syncMapOverlay();
    syncOverlayEditor();
    const map = mapInstanceRef.current;
    const handleRefresh = () => {
      syncMapOverlay();
      syncOverlayEditor();
      emitMapViewChange();
    };

    map.on('zoomend moveend resize viewreset', handleRefresh);
    return () => {
      map.off('zoomend moveend resize viewreset', handleRefresh);
    };
  }, [mapLoaded, mapOverlay, overlayEditor, onMapViewChange]);

  useEffect(() => {
    if (!mapInstanceRef.current) return undefined;

    const map = mapInstanceRef.current;
    const handleClick = (event) => {
      if (manualEditor?.isActive && manualEditor?.onMapClick) {
        manualEditor.onMapClick([event.latlng.lat, event.latlng.lng]);
        return;
      }
      if (popupStopEditor?.isActive && popupStopEditor?.onMapClick) {
        popupStopEditor.onMapClick([event.latlng.lat, event.latlng.lng]);
        return;
      }
      if (stopPlacementEditor?.isActive && stopPlacementEditor?.onMapClick) {
        stopPlacementEditor.onMapClick([event.latlng.lat, event.latlng.lng]);
      }
    };

    map.on('click', handleClick);
    return () => {
      map.off('click', handleClick);
    };
  }, [mapLoaded, manualEditor, popupStopEditor, stopPlacementEditor]);

  useEffect(() => {
    if (!mapInstanceRef.current) return undefined;

    const map = mapInstanceRef.current;
    const container = map.getContainer();

    if (popupStopEditor?.isActive) {
      map.dragging.disable();
      container.style.cursor = 'crosshair';
    } else if (manualEditor?.isActive || stopPlacementEditor?.isActive) {
      map.dragging.enable();
      container.style.cursor = 'crosshair';
    } else {
      map.dragging.enable();
      container.style.cursor = '';
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.dragging.enable();
        mapInstanceRef.current.getContainer().style.cursor = '';
      }
    };
  }, [manualEditor, popupStopEditor, stopPlacementEditor]);

  return (
    <div className="bg-white rounded-lg shadow-sm mb-6">
      <div className="p-4 border-b">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <MapPin className="w-5 h-5 text-blue-500" />
          Itinerary Map
          {points.length > 0 && (
            <span className="text-sm text-gray-500 font-normal ml-2">
              ({points.length} points)
            </span>
          )}
          {legs?.length > 0 && (
            <span className="text-xs font-normal px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full ml-1">
              {legs.length} route leg{legs.length !== 1 ? 's' : ''}
            </span>
          )}
          {mapOverlay?.imageUrl && mapOverlay?.visible !== false && (
            <span className="text-xs font-normal px-2 py-0.5 bg-sky-100 text-sky-700 rounded-full ml-1">
              Overlay active
            </span>
          )}
          {overlayEditor?.isActive && (
            <span className="text-xs font-normal px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full ml-1">
              Overlay edit mode
            </span>
          )}
          {manualEditor?.isActive && (
            <span className="text-xs font-normal px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full ml-1">
              Manual route editing
            </span>
          )}
        </h3>
      </div>

      {loadError && (
        <div className="p-4 bg-red-50 text-red-600">
          {loadError}
        </div>
      )}

      <div className="relative w-full rounded-b-lg" style={{ minHeight: '500px' }}>
        <div
          ref={mapContainerRef}
          className="w-full rounded-b-lg"
          style={{ minHeight: '500px' }}
        >
          {!mapLoaded && (
            <div className="flex items-center justify-center h-full bg-gray-100" style={{ minHeight: '500px' }}>
              <Loader className="w-6 h-6 text-gray-400 animate-spin" />
              <span className="ml-2 text-gray-500">Loading map...</span>
            </div>
          )}
        </div>

        {popupStopEditor?.isActive && (
          <div className="pointer-events-none absolute left-4 top-4 z-[500] max-w-[280px] rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 shadow-sm">
            Popup stop mode is active. Click once on the map to create a popup stop at that location.
          </div>
        )}

        {manualEditor?.isActive && manualEditor?.renderDraftOverlay && mapLoaded && mapInstanceRef.current && (
          <div className="absolute inset-0 z-[500] pointer-events-none">
            {typeof manualEditor.onSaveManualLeg === 'function' && (
              <div className="absolute left-4 top-4 pointer-events-auto max-w-[260px]">
                <div className="mb-2 rounded-xl border border-white/70 bg-slate-900/90 px-3 py-2 text-xs text-white shadow-xl backdrop-blur">
                  <div className="font-semibold">Manual route draft</div>
                  <div className="text-white/80">Drag the points on the map, then save to lock the route.</div>
                </div>
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    manualEditor.onSaveManualLeg?.();
                  }}
                  className="rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-lg ring-2 ring-white hover:bg-blue-700"
                >
                  Save Manual Leg
                </button>
              </div>
            )}
            {(() => {
              const screenPolyline = getManualDraftScreenPoints();
              if (!screenPolyline || screenPolyline.length < 2) return null;

              return (
                <>
                  {screenPolyline.map((point, index) => {
                    if (index === screenPolyline.length - 1) return null;
                    const from = screenPolyline[index];
                    const to = screenPolyline[index + 1];
                    if (!from || !to) return null;
                    const midX = (from.x + to.x) / 2;
                    const midY = (from.y + to.y) / 2;
                    return (
                      <button
                        key={`insert-${index}`}
                        type="button"
                        aria-label={`Insert waypoint between ${index + 1} and ${index + 2}`}
                        className="pointer-events-auto absolute flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-amber-500 bg-white text-amber-600 text-sm font-extrabold shadow-md hover:bg-amber-50"
                        style={{ left: midX, top: midY }}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          if (!Array.isArray(manualEditor.draftPolyline)) return;
                          manualEditor.onDraftPointInsert?.(index, [
                            (parseFloat(manualEditor.draftPolyline[index][0]) + parseFloat(manualEditor.draftPolyline[index + 1][0])) / 2,
                            (parseFloat(manualEditor.draftPolyline[index][1]) + parseFloat(manualEditor.draftPolyline[index + 1][1])) / 2,
                          ]);
                          setDraftOverlayTick((value) => value + 1);
                        }}
                      >
                        +
                      </button>
                    );
                  })}

                  {Array.isArray(manualEditor.draftPoints) && manualEditor.draftPoints.map((point, index) => {
                    const screenPoint = screenPolyline[index + 1];
                    if (!screenPoint) return null;

                    return (
                      <button
                        key={`draft-${index}`}
                        type="button"
                        aria-label={`Waypoint ${index + 1}`}
                        className="pointer-events-auto absolute flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white bg-amber-500 text-white text-xs font-bold shadow-lg hover:bg-amber-600"
                        style={{ left: screenPoint.x, top: screenPoint.y }}
                        onPointerDown={(event) => handleDraftPointPointerDown(event, index)}
                        onContextMenu={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          manualEditor.onDraftPointRemove?.(index);
                          setDraftOverlayTick((value) => value + 1);
                        }}
                      >
                        {index + 1}
                      </button>
                    );
                  })}
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

const ItineraryMapView = (props) => {
  const [mapMode, setMapMode] = useState(() => (
    process.env.REACT_APP_GOOGLE_MAP_TILES_API_KEY ? '3d' : 'flat'
  ));
  const hasGoogle3dKey = Boolean(process.env.REACT_APP_GOOGLE_MAP_TILES_API_KEY);

  return (
    <div className="relative">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><MapPin className="h-4 w-4 text-blue-500" />Map view</div>
        <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-1 text-xs font-semibold">
          <button type="button" onClick={() => setMapMode('flat')} className={`rounded-md px-3 py-1.5 ${mapMode === 'flat' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>Flat map</button>
          <button type="button" onClick={() => setMapMode('3d')} className={`rounded-md px-3 py-1.5 ${mapMode === '3d' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500'}`}>Google 3D terrain</button>
        </div>
      </div>
      {mapMode === '3d' ? (
        <div className="rounded-lg bg-white shadow-sm">
          {!hasGoogle3dKey && <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">Add <code>REACT_APP_GOOGLE_MAP_TILES_API_KEY</code> to enable Google Photorealistic 3D Tiles.</div>}
          <CesiumTerrainMap {...props} />
        </div>
      ) : <LeafletItineraryMapView {...props} />}
    </div>
  );
};

export default ItineraryMapView;
